import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo, LogoMark } from "./Logo";

const LINKS = [
  { label: "Servizi", id: "#servizi" },
  { label: "Demo", id: "/demo" },
  { label: "Progetti", id: "#casi-studio" },
  { label: "Il Metodo", id: "#metodo" },
  { label: "Piani", id: "#piani" },
];

const testId = (id) => id.replace(/[#/]/g, "");

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, lenis]);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (id === "#top") {
      lenis?.scrollTo(0, { duration: 1.4 });
    } else {
      lenis?.scrollTo(id, { offset: -84, duration: 1.4 });
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass border-b border-line" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8 md:h-20">
          <Logo onClick={go} />

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigazione principale">
            {LINKS.map((l) =>
              l.id.startsWith("/") ? (
                <Link
                  key={l.id}
                  to={l.id}
                  data-testid={`navbar-link-${testId(l.id)}`}
                  className="group relative font-accent text-[11px] uppercase tracking-[0.24em] text-ochre transition-colors duration-300 hover:text-bone"
                >
                  {l.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.id}
                  onClick={go(l.id)}
                  data-testid={`navbar-link-${testId(l.id)}`}
                  className="group relative font-accent text-[11px] uppercase tracking-[0.24em] text-stone transition-colors duration-300 hover:text-bone"
                >
                  {l.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
                </a>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contatti"
              onClick={go("#contatti")}
              data-testid="navbar-cta-button"
              className="group hidden items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-all duration-300 hover:bg-[#f0695a] sm:inline-flex"
            >
              Richiedi audit gratuito
              <ArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="navbar-menu-button"
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              aria-expanded={open}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-bone transition-colors duration-300 hover:border-terracotta lg:hidden"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-ink/97 px-6 pt-24 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-1 flex-col justify-center gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.id}
                  onClick={l.id.startsWith("/") ? () => setOpen(false) : go(l.id)}
                  data-testid={`navbar-mobile-link-${testId(l.id)}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="border-b border-line py-5 font-serif text-4xl tracking-tight text-bone transition-colors duration-300 hover:text-terracotta"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#contatti"
                onClick={go("#contatti")}
                data-testid="navbar-mobile-cta"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-terracotta px-7 py-4 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08]"
              >
                Richiedi audit gratuito
                <ArrowUpRight size={16} />
              </motion.a>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-3 pb-10 text-faded"
            >
              <LogoMark size={22} />
              <span className="font-accent text-[10px] uppercase tracking-[0.3em]">
                Venezia · Mestre · Veneto
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}